import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useEffectiveAccess } from "@/hooks/useEffectiveAccess";
import { useInstitutionAdminCheck } from "@/hooks/useInstitutionAdminCheck";
import { useSchoolAdminCheck } from "@/hooks/useSchoolAdminCheck";
import { useLanguage } from "@/contexts/LanguageContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";

const LOADING_TIMEOUT = 12000;

export const InstitutionAdminRoute = ({ children }: { children: React.ReactNode }) => {
  const access = useEffectiveAccess();
  const { isInstitutionAdmin, loading: instAdminLoading } = useInstitutionAdminCheck();
  const { isSchoolAdmin, loading: schoolAdminLoading } = useSchoolAdminCheck();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const hasActedRef = useRef(false);
  const [timedOut, setTimedOut] = useState(false);

  const roleLoading = instAdminLoading || schoolAdminLoading;
  const allLoading = access.status === "loading" || roleLoading;
  const isAdmin = access.role === "admin";
  const hasAccess = isAdmin || isInstitutionAdmin || isSchoolAdmin;

  useEffect(() => {
    if (!allLoading) { setTimedOut(false); return; }
    const timer = setTimeout(() => setTimedOut(true), LOADING_TIMEOUT);
    return () => clearTimeout(timer);
  }, [allLoading]);

  useEffect(() => {
    if (allLoading) return;

    // Engine handles guest/unconfirmed redirects
    if (access.redirectTo) {
      navigate(access.redirectTo, { replace: true });
      return;
    }

    if (!hasAccess && !hasActedRef.current) {
      hasActedRef.current = true;
      if (access.role === "institutional" || access.role === "cohort") {
        navigate('/dashboard', { replace: true });
      } else {
        toast.error(t("admin.accessDenied") || "Access denied");
        navigate("/dashboard", { replace: true });
      }
    }
  }, [access, hasAccess, allLoading, navigate, t]);

  useEffect(() => {
    hasActedRef.current = false;
  }, [access.input.user?.id]);

  if (allLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">{t("admin.verifyingPermissions") || t("common.loading")}</p>
          {timedOut && (
            <Button variant="outline" size="sm" className="mt-4 gap-2" onClick={() => window.location.reload()}>
              <RefreshCw className="w-4 h-4" />
              {t("common.retry") || "Reintentar"}
            </Button>
          )}
        </div>
      </div>
    );
  }

  return hasAccess ? <>{children}</> : null;
};
